/**
 * Design kit for the invoice module.
 *
 * The rest of the app is MUI v4, which gives every screen the same grey
 * card-and-table look. Money screens need their own voice: amounts have to
 * read as the loudest thing on the page, status has to be legible at a
 * glance, and the staff console has to stay dense while the creator view
 * stays calm. So this module ships its own tokens and primitives in one
 * scoped stylesheet instead of fighting the theme.
 *
 * Everything is namespaced under `.iv-` and only applies inside `.iv-root`.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';

export const STATUS_ORDER = ['submitted', 'accepted', 'approved', 'sent_for_payment', 'paid'];

export const STATUS_META = {
  submitted: { label: 'Submitted', tone: 'amber' },
  accepted: { label: 'Accepted', tone: 'cyan' },
  approved: { label: 'Approved', tone: 'blue' },
  sent_for_payment: { label: 'Sent for payment', tone: 'violet' },
  paid: { label: 'Paid', tone: 'green' },
  rejected: { label: 'Rejected', tone: 'red' },
};

/* ------------------------------------------------------------------ *
 * Stylesheet
 * ------------------------------------------------------------------ */

export const KIT_CSS = `
.iv-root, .iv-root *, .iv-root *::before, .iv-root *::after { box-sizing: border-box; }

/* Scoped reset. This app ships global bare-element CSS from feature
   stylesheets that CRA bundles into every page. The worst is a bare
   'section' rule in CompetitorReportAnalysis.style.css sizing every section
   to 100vw x 100vh and centring it as a print slide, which turned each
   section of this page into a full-viewport centred block; App.css also sets
   text-align:center on .App, which centres every string. Neutralise the
   elements this module uses rather than trusting no global rule targets
   them.

   Every selector below is wrapped in :where() so the reset contributes no
   specificity of its own. It still outranks a bare element rule, but a
   component class further down this sheet always wins -- without that, a
   blanket '.iv-root p' margin reset outranks .iv-empty__text's auto margins
   and quietly flattens spacing across the module. */
.iv-root { text-align: left; }
.iv-root :where(section) {
  display: block; width: auto; height: auto; min-height: 0;
  background: none; align-items: normal; justify-content: normal;
  flex-direction: row; page-break-after: auto; break-after: auto;
  padding: 0; margin: 0;
}
.iv-root :where(dl, dd, dt) { display: block; margin: 0; }
.iv-root :where(ul, ol) { list-style: none; margin: 0; padding: 0; }
.iv-root :where(table) { border-collapse: collapse; width: 100%; }
.iv-root :where(h1, h2, h3, p) { margin: 0; }
.iv-root :where(button, input, textarea, select) { font: inherit; color: inherit; }
.iv-root :where(img) { max-width: 100%; }

.iv-root {
  --ink:        #0B0D12;
  --ink-2:      #3F4654;
  --ink-3:      #737C8C;
  --ink-4:      #A3ABBA;
  --surface:    #FFFFFF;
  --canvas:     #F5F6F8;
  --line:       #E6E9EF;
  --line-2:     #D3D8E2;
  --accent:     #1E9CB2;
  --accent-ink: #12707F;
  --accent-bg:  #E8F6F8;
  --radius:     14px;
  --radius-sm:  10px;
  --shadow-sm:  0 1px 2px rgba(11,13,18,.05);
  --shadow-md:  0 4px 16px -4px rgba(11,13,18,.10), 0 1px 3px rgba(11,13,18,.06);
  --shadow-lg:  0 24px 60px -18px rgba(11,13,18,.28);

  font-family: 'Lato', -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  color: var(--ink-2);
  -webkit-font-smoothing: antialiased;
}

/* Money and dates line up in columns only with tabular figures. */
.iv-num { font-variant-numeric: tabular-nums; font-feature-settings: 'tnum' 1; }

@keyframes iv-rise   { from { opacity: 0; transform: translate3d(0,10px,0); } to { opacity: 1; transform: none; } }
@keyframes iv-shim   { 100% { transform: translateX(100%); } }
@keyframes iv-spin   { to { transform: rotate(360deg); } }
@keyframes iv-pulse  { 0%,100% { opacity: 1; } 50% { opacity: .45; } }

/* --- layout ------------------------------------------------------- */
.iv-page  { min-height: 100vh; background: var(--canvas); padding: 32px 20px 96px; }
.iv-wrap  { margin: 0 auto; width: 100%; }
.iv-wrap--reading { max-width: 940px; }
.iv-wrap--console { max-width: 1440px; }

.iv-head { display: flex; align-items: flex-end; justify-content: space-between; gap: 20px; flex-wrap: wrap; margin-bottom: 24px; }
.iv-h1 { font-size: 27px; line-height: 1.15; font-weight: 800; letter-spacing: -.022em; color: var(--ink); margin: 0; }
.iv-lede { font-size: 14.5px; line-height: 1.55; color: var(--ink-3); margin: 7px 0 0; max-width: 62ch; }
.iv-h2 { font-size: 13px; font-weight: 800; letter-spacing: .07em; text-transform: uppercase; color: var(--ink-3); margin: 0; display: flex; align-items: center; gap: 9px; }
.iv-h2__count { font-size: 11.5px; letter-spacing: 0; background: var(--line); color: var(--ink-2); border-radius: 999px; padding: 2px 8px; font-weight: 800; }
.iv-root .iv-section { margin-top: 34px; }
.iv-section__bar { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 14px; }

/* --- buttons ------------------------------------------------------ */
.iv-btn {
  font-family: inherit; font-weight: 700; font-size: 13.5px; line-height: 1;
  border-radius: var(--radius-sm); border: 1px solid transparent; cursor: pointer;
  padding: 10px 16px; display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  transition: background .15s ease, border-color .15s ease, color .15s ease, transform .12s ease, box-shadow .15s ease;
  white-space: nowrap; text-decoration: none;
}
.iv-btn:disabled { opacity: .55; cursor: not-allowed; }
.iv-btn:focus-visible { outline: 3px solid rgba(30,156,178,.4); outline-offset: 2px; }
.iv-btn--primary { background: var(--accent); color: #fff; box-shadow: 0 1px 2px rgba(11,13,18,.10); }
.iv-btn--primary:hover:not(:disabled) { background: var(--accent-ink); transform: translateY(-1px); box-shadow: var(--shadow-md); }
.iv-btn--primary:active:not(:disabled) { transform: none; }
.iv-btn--ghost { background: var(--surface); color: var(--ink-2); border-color: var(--line-2); }
.iv-btn--ghost:hover:not(:disabled) { border-color: var(--ink-4); color: var(--ink); }
.iv-btn--quiet { background: transparent; color: var(--ink-3); padding: 8px 10px; }
.iv-btn--quiet:hover:not(:disabled) { background: var(--line); color: var(--ink); }
.iv-btn--link { background: none; border: 0; padding: 0; color: var(--accent-ink); font-weight: 700; cursor: pointer; font-size: 13.5px; font-family: inherit; }
.iv-btn--link:hover { text-decoration: underline; }
.iv-btn--danger { background: #fff; color: #B91C1C; border-color: #F3C6C6; }
.iv-btn--danger:hover:not(:disabled) { background: #FEF2F2; border-color: #E79A9A; }
.iv-btn--sm { padding: 7px 11px; font-size: 12.5px; }

.iv-spin { width: 14px; height: 14px; border-radius: 50%; border: 2px solid currentColor; border-top-color: transparent; animation: iv-spin .7s linear infinite; flex: none; }

/* --- surfaces ----------------------------------------------------- */
.iv-card { background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius); box-shadow: var(--shadow-sm); }
.iv-card--flush { border-radius: var(--radius); overflow: hidden; }

/* --- status ------------------------------------------------------- */
.iv-pill {
  display: inline-flex; align-items: center; gap: 6px; border-radius: 999px;
  padding: 4px 11px 4px 9px; font-size: 11.5px; font-weight: 800; letter-spacing: .01em; white-space: nowrap;
}
.iv-pill__dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; flex: none; }
.iv-pill--amber  { color: #A15C07; background: #FEF3C7; }
.iv-pill--cyan   { color: #0E7490; background: #CFFAFE; }
.iv-pill--blue   { color: #1D4ED8; background: #DBEAFE; }
.iv-pill--violet { color: #6D28D9; background: #EDE9FE; }
.iv-pill--green  { color: #15803D; background: #DCFCE7; }
.iv-pill--red    { color: #B91C1C; background: #FEE2E2; }
.iv-pill--grey   { color: var(--ink-2); background: var(--line); }
.iv-pill--live .iv-pill__dot { animation: iv-pulse 1.8s ease-in-out infinite; }

/* --- brand mark --------------------------------------------------- */
.iv-mark {
  width: 42px; height: 42px; border-radius: 11px; flex: none; overflow: hidden;
  border: 1px solid var(--line); background: #FBFCFD;
  display: grid; place-items: center; font-weight: 800; font-size: 16px; color: var(--accent-ink);
}
.iv-mark img { width: 100%; height: 100%; object-fit: contain; display: block; }
.iv-mark--sm { width: 30px; height: 30px; border-radius: 8px; font-size: 12px; }

/* --- pipeline tracker --------------------------------------------- */
.iv-track { display: flex; align-items: center; gap: 0; margin-top: 14px; }
.iv-track__node { display: flex; align-items: center; gap: 7px; flex: none; }
.iv-track__dot {
  width: 15px; height: 15px; border-radius: 50%; display: grid; place-items: center; flex: none;
  border: 2px solid var(--line-2); background: var(--surface); color: #fff;
  transition: background .2s ease, border-color .2s ease;
}
.iv-track__dot svg { width: 8px; height: 8px; }
.iv-track__label { font-size: 11.5px; font-weight: 700; color: var(--ink-4); white-space: nowrap; }
.iv-track__link { flex: 1 1 auto; height: 2px; background: var(--line); min-width: 12px; margin: 0 8px; border-radius: 2px; }
.iv-track__node--done .iv-track__dot { background: var(--accent); border-color: var(--accent); }
.iv-track__node--done .iv-track__label { color: var(--ink-2); }
.iv-track__node--now .iv-track__dot { border-color: var(--accent); box-shadow: 0 0 0 4px var(--accent-bg); }
.iv-track__node--now .iv-track__label { color: var(--accent-ink); font-weight: 800; }
.iv-track__link--done { background: var(--accent); }
.iv-track--dead .iv-track__dot { border-color: #E79A9A; background: #fff; }
.iv-track--dead .iv-track__node--done .iv-track__dot { background: #DC2626; border-color: #DC2626; }
.iv-track--dead .iv-track__link--done { background: #DC2626; }
.iv-track--dead .iv-track__node--now .iv-track__dot { border-color: #DC2626; box-shadow: 0 0 0 4px #FEE2E2; }
.iv-track--dead .iv-track__node--now .iv-track__label { color: #B91C1C; }

/* --- skeleton ----------------------------------------------------- */
.iv-sk { position: relative; overflow: hidden; background: #EDEFF3; border-radius: 6px; }
.iv-sk::after {
  content: ''; position: absolute; inset: 0; transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.65), transparent);
  animation: iv-shim 1.4s infinite;
}

/* --- empty / error ------------------------------------------------ */
.iv-empty { text-align: center; padding: 52px 28px; }
.iv-empty__icon { width: 46px; height: 46px; margin: 0 auto 16px; border-radius: 13px; background: var(--accent-bg); color: var(--accent-ink); display: grid; place-items: center; }
.iv-empty__title { font-size: 16px; font-weight: 800; color: var(--ink); margin: 0 0 7px; }
.iv-empty__text { font-size: 13.5px; line-height: 1.6; color: var(--ink-3); margin: 0 auto; max-width: 46ch; }

.iv-alert { display: flex; gap: 12px; align-items: flex-start; padding: 14px 16px; border-radius: var(--radius-sm); background: #FEF2F2; border: 1px solid #F6D5D5; color: #991B1B; font-size: 13.5px; line-height: 1.55; }

/* --- inputs ------------------------------------------------------- */
.iv-field { display: block; }
.iv-label { display: block; font-size: 12px; font-weight: 800; letter-spacing: .03em; text-transform: uppercase; color: var(--ink-3); margin-bottom: 6px; }
.iv-input, .iv-select, .iv-textarea {
  width: 100%; font-family: inherit; font-size: 14px; color: var(--ink);
  background: var(--surface); border: 1px solid var(--line-2); border-radius: var(--radius-sm);
  padding: 10px 12px; transition: border-color .15s ease, box-shadow .15s ease;
}
.iv-input:focus, .iv-select:focus, .iv-textarea:focus { outline: none; border-color: var(--accent); box-shadow: 0 0 0 3px rgba(30,156,178,.16); }
.iv-input::placeholder, .iv-textarea::placeholder { color: var(--ink-4); }
.iv-input--invalid, .iv-textarea--invalid { border-color: #DC2626; }
.iv-hint { display: block; font-size: 12px; color: var(--ink-3); margin-top: 6px; line-height: 1.5; }
.iv-hint--error { color: #B91C1C; font-weight: 700; }
.iv-search { position: relative; }
.iv-search .iv-input { padding-left: 36px; }
.iv-search__icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--ink-4); pointer-events: none; display: grid; }
.iv-search__kbd {
  position: absolute; right: 9px; top: 50%; transform: translateY(-50%);
  font-size: 11px; font-weight: 700; color: var(--ink-4); border: 1px solid var(--line-2);
  border-radius: 5px; padding: 1px 6px; background: var(--canvas); pointer-events: none;
}

/* --- dialog ------------------------------------------------------- */
.iv-scrim { position: fixed; inset: 0; background: rgba(11,13,18,.5); backdrop-filter: blur(2px); z-index: 1300; display: flex; align-items: center; justify-content: center; padding: 24px; animation: iv-rise .18s ease both; }
.iv-modal { background: var(--surface); border-radius: 18px; box-shadow: var(--shadow-lg); width: 100%; max-height: calc(100vh - 48px); display: flex; flex-direction: column; animation: iv-rise .22s cubic-bezier(.2,.8,.3,1) both; }
.iv-modal--sm { max-width: 460px; }
.iv-modal--md { max-width: 620px; }
.iv-modal--lg { max-width: 980px; height: calc(100vh - 48px); }
.iv-modal__head { padding: 20px 24px 16px; border-bottom: 1px solid var(--line); display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex: none; }
.iv-modal__title { font-size: 17px; font-weight: 800; color: var(--ink); margin: 0; letter-spacing: -.01em; }
.iv-modal__sub { font-size: 13px; color: var(--ink-3); margin: 4px 0 0; }
.iv-modal__body { padding: 22px 24px; overflow: auto; flex: 1 1 auto; }
.iv-modal__foot { padding: 16px 24px; border-top: 1px solid var(--line); display: flex; justify-content: flex-end; gap: 10px; flex: none; background: #FCFCFD; border-radius: 0 0 18px 18px; }
.iv-x { border: 0; background: transparent; cursor: pointer; color: var(--ink-4); padding: 6px; border-radius: 8px; display: grid; line-height: 0; flex: none; }
.iv-x:hover { background: var(--line); color: var(--ink); }

@media (max-width: 640px) {
  /* Five labelled steps cannot fit a phone, and the status pill directly
     above already names the step -- so keep the dots and drop the labels. */
  .iv-track__label { display: none; }
  .iv-track__link { min-width: 8px; margin: 0 5px; }
  .iv-track { gap: 2px; }

  .iv-page { padding: 20px 14px 80px; }
  .iv-h1 { font-size: 23px; }
  .iv-scrim { padding: 0; align-items: flex-end; }
  .iv-modal { max-width: none; border-radius: 18px 18px 0 0; max-height: 92vh; }
  .iv-modal--lg { height: 92vh; }
  .iv-modal__foot { border-radius: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .iv-root *, .iv-scrim, .iv-modal { animation-duration: .001ms !important; transition-duration: .001ms !important; }
  .iv-sk::after { animation: none; }
}
`;

/* ------------------------------------------------------------------ *
 * Primitives
 * ------------------------------------------------------------------ */

/** Injects a stylesheet once per document, however many pages mount it. */
export function useStyleSheet(id, css) {
  useEffect(() => {
    if (document.getElementById(id)) return undefined;
    const tag = document.createElement('style');
    tag.id = id;
    tag.textContent = css;
    document.head.appendChild(tag);
    return undefined;
  }, [id, css]);
}

/**
 * The app pins a 1280px viewport in index.html, so media queries never fire
 * on a phone. Creators open invoices on their phone, so this page restores a
 * real viewport while it is mounted and puts it back on the way out.
 */
export function useResponsiveViewport() {
  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    if (!meta) return undefined;
    const previous = meta.getAttribute('content');
    meta.setAttribute('content', 'width=device-width, initial-scale=1');
    return () => meta.setAttribute('content', previous);
  }, []);
}

export function Icon({ path, size = 16, fill = false }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill ? 'currentColor' : 'none'}
      stroke={fill ? 'none' : 'currentColor'}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {path}
    </svg>
  );
}

export const ICONS = {
  check: <polyline points="20 6 9 17 4 12" />,
  x: (
    <>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </>
  ),
  receipt: (
    <>
      <path d="M4 3v18l2.5-1.6L9 21l2.5-1.6L14 21l2.5-1.6L19 21V3z" />
      <line x1="8" y1="8" x2="15" y2="8" />
      <line x1="8" y1="12" x2="15" y2="12" />
    </>
  ),
  sheet: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="9" y1="9" x2="9" y2="21" />
    </>
  ),
  download: (
    <>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </>
  ),
  eye: (
    <>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 15 14" />
    </>
  ),
  pencil: (
    <>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
    </>
  ),
  chevron: <polyline points="6 9 12 15 18 9" />,
  alert: (
    <>
      <circle cx="12" cy="12" r="9" />
      <line x1="12" y1="8" x2="12" y2="13" />
      <line x1="12" y1="16.5" x2="12.01" y2="16.5" />
    </>
  ),
  plus: (
    <>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </>
  ),
  arrow: (
    <>
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </>
  ),
};

export function StatusPill({ status, label }) {
  const meta = STATUS_META[status] || { label: label || status, tone: 'grey' };
  const live = status === 'submitted' || status === 'sent_for_payment';
  return (
    <span className={`iv-pill iv-pill--${meta.tone}${live ? ' iv-pill--live' : ''}`}>
      <span className="iv-pill__dot" />
      {label || meta.label}
    </span>
  );
}

export function BrandMark({ name, src, small }) {
  const [broken, setBroken] = useState(false);
  const initial = (name || '?').trim().charAt(0).toUpperCase() || '?';
  return (
    <span className={`iv-mark${small ? ' iv-mark--sm' : ''}`} aria-hidden="true">
      {src && !broken ? <img src={src} alt="" onError={() => setBroken(true)} /> : initial}
    </span>
  );
}

/**
 * The five-step payment pipeline. A rejected invoice stops wherever it was
 * rejected and turns red rather than pretending the remaining steps are
 * still ahead of it.
 */
export function StatusTrack({ status, compact }) {
  const rejected = status === 'rejected';
  const reached = rejected ? 0 : STATUS_ORDER.indexOf(status);
  const steps = compact ? STATUS_ORDER.filter((s) => s !== 'accepted') : STATUS_ORDER;

  return (
    <div className={`iv-track${rejected ? ' iv-track--dead' : ''}`}>
      {rejected ? (
        // Every invoice was submitted before it could be rejected, so show
        // that leg rather than leaving a single dot floating on its own.
        <>
          <div className="iv-track__node iv-track__node--done">
            <span className="iv-track__dot">
              <Icon path={ICONS.check} size={8} />
            </span>
            <span className="iv-track__label">Submitted</span>
          </div>
          <span className="iv-track__link iv-track__link--done" />
          <div className="iv-track__node iv-track__node--done iv-track__node--now">
            <span className="iv-track__dot">
              <Icon path={ICONS.x} size={8} />
            </span>
            <span className="iv-track__label">Rejected</span>
          </div>
        </>
      ) : (
        steps.map((step, index) => {
          const at = STATUS_ORDER.indexOf(step);
          const done = at <= reached;
          const now = at === reached;
          return (
            <React.Fragment key={step}>
              {index > 0 ? (
                <span className={`iv-track__link${done ? ' iv-track__link--done' : ''}`} />
              ) : null}
              <div
                className={`iv-track__node${done ? ' iv-track__node--done' : ''}${
                  now ? ' iv-track__node--now' : ''
                }`}
              >
                <span className="iv-track__dot">
                  {done ? <Icon path={ICONS.check} size={8} /> : null}
                </span>
                <span className="iv-track__label">{STATUS_META[step].label}</span>
              </div>
            </React.Fragment>
          );
        })
      )}
    </div>
  );
}

export function Empty({ icon, title, children }) {
  return (
    <div className="iv-empty">
      <div className="iv-empty__icon">
        <Icon path={icon || ICONS.receipt} size={22} />
      </div>
      <p className="iv-empty__title">{title}</p>
      <p className="iv-empty__text">{children}</p>
    </div>
  );
}

export function Alert({ children, onRetry }) {
  return (
    <div className="iv-alert" role="alert">
      <span style={{ flex: 'none', marginTop: 1 }}>
        <Icon path={ICONS.alert} size={17} />
      </span>
      <span style={{ flex: 1 }}>{children}</span>
      {onRetry ? (
        <button
          type="button"
          className="iv-btn--link"
          style={{ color: '#991B1B' }}
          onClick={onRetry}
        >
          Retry
        </button>
      ) : null}
    </div>
  );
}

/** Modal with a scrim, Escape-to-close and focus returned to the opener. */
/**
 * `tone` is optional and purely cosmetic: it adds a modifier class so a
 * feature can dress its own dialog without forking this one. Callers that
 * omit it get exactly what they got before.
 */
export function Modal({ open, onClose, title, subtitle, size = 'md', tone, footer, children }) {
  const panel = useRef(null);
  const opener = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    opener.current = document.activeElement;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (panel.current) panel.current.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      if (opener.current && opener.current.focus) opener.current.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="iv-root iv-scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`iv-modal iv-modal--${size}${tone ? ` iv-modal--${tone}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={panel}
      >
        <div className="iv-modal__head">
          <div>
            <h2 className="iv-modal__title">{title}</h2>
            {subtitle ? <p className="iv-modal__sub">{subtitle}</p> : null}
          </div>
          <button type="button" className="iv-x" onClick={onClose} aria-label="Close">
            <Icon path={ICONS.x} size={18} />
          </button>
        </div>
        <div className="iv-modal__body">{children}</div>
        {footer ? <div className="iv-modal__foot">{footer}</div> : null}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Formatting
 * ------------------------------------------------------------------ */

const INR = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

/** `₹1,20,000`. Compact turns big figures into `₹1.2L` / `₹1.2Cr`. */
export function money(value, { compact = false } = {}) {
  const n = Number(value) || 0;
  if (compact) {
    if (Math.abs(n) >= 1e7) return `₹${(n / 1e7).toFixed(n % 1e7 === 0 ? 0 : 1)}Cr`;
    if (Math.abs(n) >= 1e5) return `₹${(n / 1e5).toFixed(n % 1e5 === 0 ? 0 : 1)}L`;
  }
  return `₹${INR.format(n)}`;
}

export function shortDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** "2 hours ago" / "in 6 hours" — used for edit windows and history. */
export function relative(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const diff = d.getTime() - Date.now();
  const abs = Math.abs(diff);
  const mins = Math.round(abs / 60000);
  if (mins < 1) return diff > 0 ? 'in a moment' : 'just now';
  if (mins < 60) return diff > 0 ? `in ${mins} min` : `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return diff > 0 ? `in ${hours}h` : `${hours}h ago`;
  const days = Math.round(hours / 24);
  return diff > 0 ? `in ${days}d` : `${days}d ago`;
}

/** Live countdown to a deadline; re-renders each minute while it matters. */
export function useCountdown(deadline) {
  const target = useMemo(() => (deadline ? new Date(deadline).getTime() : 0), [deadline]);
  const [, tick] = useState(0);

  useEffect(() => {
    if (!target || target <= Date.now()) return undefined;
    const id = setInterval(() => tick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, [target]);

  if (!target) return null;
  const left = target - Date.now();
  if (left <= 0) return null;
  const hours = Math.floor(left / 3600000);
  const mins = Math.floor((left % 3600000) / 60000);
  return hours >= 1 ? `${hours}h ${mins}m` : `${mins}m`;
}
